import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/envelope';
import { REFRESH_FLOOR_MS } from '../../lib/useLoad';
import { draftSummaryOf, seedDraftSummaries } from './drafts';
import type {
  Conversation,
  ConversationMeta,
  ConversationStatus,
  CrmContact,
  Message,
} from '../../api/types';
import type { CommsSource } from './source';

export interface ConversationQuery {
  /** Several is legal: 'All open' widens to the assistant's queue for an agent following it. */
  status: ConversationStatus | ConversationStatus[] | 'all';
  /** Empty = every inbox. One = server-side filter. Many = fan-out + merge. */
  inboxIds: number[];
  assigneeType: 'all' | 'me' | 'unassigned';
  /** The "Handled by chatbot" view: every conversation the assistant answered. */
  botHandled?: boolean;
  /** The Sent view: conversations the team has replied to at least once. */
  sent?: boolean;
  /** The Starred view: conversations the team flagged, across every status. */
  starred?: boolean;
  /** The Drafts view: conversations this agent left a reply unsent on. */
  hasDraft?: boolean;
  /** The rail's label filter: rows carrying ANY of these, as the server reads it. */
  labels?: string[];
  q: string;
  sortBy: 'latest' | 'created_at' | 'waiting_since' | 'draft_updated' | 'last_outgoing';
}

export interface ConversationsState {
  rows: Conversation[];
  meta: ConversationMeta | null;
  loading: boolean;
  /**
   * Loading because the FILTER changed — `rows` still belong to the previous
   * inbox/view, so painting them is painting the wrong list. Views skeleton.
   */
  stale: boolean;
  /** Loading the same filter again — `rows` are right, just possibly older. */
  refreshing: boolean;
  error: ApiError | null;
  hasMore: boolean;
  /** A `loadMore` page is in flight — the rows already on screen stay put. */
  loadingMore: boolean;
  loadMore: () => void;
  reload: () => void;
  /** Zero the unread badge locally (the server call is the view's job). */
  markSeenLocal: (displayId: number) => void;
  /**
   * The same for a whole channel set — "mark all read" paints before it lands.
   * An empty set means every channel, matching what the server was asked.
   */
  markAllSeenLocal: (inboxIds: number[]) => void;
}

const PAGE_SIZE = 25;

/**
 * Switching mailboxes is a there-and-back move (check the other number, come
 * straight back), so the last few filter results are worth keeping: a revisit
 * repaints from memory on the click and revalidates behind it, instead of
 * emptying the pane for a round trip. Entries older than the TTL are dropped
 * rather than shown — realtime only keeps the ACTIVE filter's rows live, so a
 * long-parked entry can be wrong about unread counts and last messages.
 */
const CACHE_TTL_MS = 60_000;
const CACHE_MAX = 24;

interface CacheEntry {
  rows: Conversation[];
  meta: ConversationMeta | null;
  hasMore: boolean;
  at: number;
}

const cache = new Map<string, CacheEntry>();

/**
 * Every key is scoped to the source that produced it. Signing out and back in
 * as a different tenant builds a new source without reloading the page, and
 * rows from the old one must never be reachable — an id per source object makes
 * that structural rather than something a cleanup effect has to remember.
 */
const sourceIds = new WeakMap<CommsSource, number>();
let nextSourceId = 0;

function sourceId(source: CommsSource): number {
  let id = sourceIds.get(source);
  if (id === undefined) {
    id = ++nextSourceId;
    sourceIds.set(source, id);
  }
  return id;
}

/**
 * The view flags are part of the key, not just status/assignee/sort/q: a
 * widened search DROPS them and changes nothing else, so 'Sent' with a search
 * in the box and 'Sent, searched everywhere' would otherwise share one entry
 * and paint each other's rows for the TTL.
 */
const cacheKey = (source: CommsSource, query: ConversationQuery) =>
  [
    sourceId(source),
    query.status,
    query.assigneeType,
    query.sortBy,
    query.botHandled ? 'bot' : '',
    query.hasDraft ? 'draft' : '',
    query.sent ? 'sent' : '',
    query.starred ? 'starred' : '',
    [...(query.labels ?? [])].sort().join('+'),
    query.q,
    [...query.inboxIds].sort((a, b) => a - b).join('.'),
  ].join('|');

function cacheRead(key: string): CacheEntry | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.at > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry;
}

/**
 * Apply a local row edit to every cached filter holding that conversation.
 * Without this, "open a thread, switch mailbox, switch back" repaints the
 * unread badge the agent just cleared until the revalidation lands — the one
 * stale value that reads as the UI undoing the user's own action.
 */
function cachePatchRow(displayId: number, patch: Partial<Conversation>): void {
  for (const [key, entry] of cache) {
    if (!entry.rows.some((row) => row.id === displayId)) continue;
    cache.set(key, {
      ...entry,
      rows: entry.rows.map((row) => (row.id === displayId ? { ...row, ...patch } : row)),
    });
  }
}

/** The same for a person: every cached row that shows them takes the fresh copy. */
function cachePatchContact(contact: CrmContact): void {
  for (const [key, entry] of cache) {
    if (!entry.rows.some((row) => row.contact.id === contact.id)) continue;
    cache.set(key, {
      ...entry,
      rows: entry.rows.map((row) => (row.contact.id === contact.id ? { ...row, contact } : row)),
    });
  }
}

function cacheWrite(key: string, entry: CacheEntry): void {
  cache.delete(key);
  cache.set(key, entry);
  // Map keeps insertion order, so the first key is the least recently written.
  while (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value as string);
}

const EMPTY_META: ConversationMeta = {
  mineCount: 0,
  unassignedCount: 0,
  allCount: 0,
  pendingCount: 0,
  botHandledCount: 0,
  draftCount: 0,
};

function sortRows(rows: Conversation[], sortBy: ConversationQuery['sortBy']): Conversation[] {
  const time = (value: string | null) => (value ? new Date(value).getTime() : 0);
  const sorted = [...rows];
  if (sortBy === 'waiting_since') {
    // Oldest waiting first; conversations without waitingSince sink.
    sorted.sort(
      (a, b) =>
        (a.waitingSince ? time(a.waitingSince) : Number.POSITIVE_INFINITY) -
        (b.waitingSince ? time(b.waitingSince) : Number.POSITIVE_INFINITY),
    );
  } else if (sortBy === 'last_outgoing') {
    // Newest reply first; a conversation nobody has answered sinks — the same
    // `desc nulls last` the server orders by.
    sorted.sort((a, b) => time(b.lastOutgoingAt) - time(a.lastOutgoingAt));
  } else if (sortBy === 'created_at') {
    sorted.sort((a, b) => time(b.createdAt) - time(a.createdAt));
  } else if (sortBy === 'draft_updated') {
    // The client's copy of the same order the server applied — used when rows
    // from several mailboxes are merged, and when a live event re-sorts them.
    // From the store rather than the row: a row replaced by a socket update
    // carries no draft key, and would otherwise sink to the bottom of the one
    // list that is ordered by it.
    const at = (row: Conversation) => time(draftSummaryOf(row.id)?.updatedAt ?? null);
    sorted.sort((a, b) => at(b) - at(a));
  } else {
    sorted.sort((a, b) => time(b.lastActivityAt) - time(a.lastActivityAt));
  }
  return sorted;
}

function matchesQuery(row: Conversation, query: ConversationQuery, meId: number | null): boolean {
  // Deleted belongs to no view, ahead of every other test — this is what takes
  // the row out of a list already on screen when somebody deletes the thread,
  // since the `conversation.updated` that follows a delete carries the row like
  // any other update. Server reads never return one, so this only ever fires on
  // a socket payload or a reconnect delta.
  if (row.deletedAt) return false;
  // Mirrors the server's filter, list of statuses included — comparing an array
  // to a string here would reject every row and quietly stop live updates in
  // the one view that asked for two statuses.
  if (query.status !== 'all') {
    const wanted = Array.isArray(query.status) ? query.status : [query.status];
    if (!wanted.includes(row.status)) return false;
  }
  if (query.inboxIds.length > 0 && !query.inboxIds.includes(row.inboxId)) return false;
  if (query.botHandled && !row.botHandled) return false;
  // Mirrors the server's `sent` filter. The column rides on every conversation
  // payload, socket ones included, so a thread joins the Sent list on the
  // `conversation.updated` that follows the reply that put it there.
  if (query.sent && !row.lastOutgoingAt) return false;
  // Mirrors the server's `starred` filter. The flag is shared and rides every
  // conversation payload, so an unstarred thread leaves this list on the socket
  // update that unstarred it, whoever pressed it.
  if (query.starred && !row.starred) return false;
  // Mirrors the server's `labels` filter, which is ANY of them. The set rides
  // every conversation payload, so a thread labelled or unlabelled by anyone
  // joins or leaves this list on the `conversation.updated` that followed it.
  if (query.labels?.length && !query.labels.some((title) => row.labels.includes(title))) {
    return false;
  }
  // Only when the store KNOWS there is no draft. A socket payload carries none
  // (a draft belongs to one agent; a broadcast is for everyone who can see the
  // conversation), so `undefined` must leave the row where it is.
  if (query.hasDraft && draftSummaryOf(row.id) === null) return false;
  // The assistant's conversations have no human assignee but are not
  // unassigned — somebody has them, and the server says the same.
  if (query.assigneeType === 'unassigned' && (row.assigneeDashBoardId !== null || row.assigneeIsBot)) {
    return false;
  }
  if (query.assigneeType === 'me' && (meId === null || row.assigneeDashBoardId !== meId)) {
    return false;
  }
  if (query.q) {
    // Mirrors the server's `q` filter — name, email, digits-only phone, and the
    // mail subject. If these drift, a socket update during a search silently
    // drops a row the server would have returned.
    const needle = query.q.toLowerCase();
    const subject =
      typeof row.additionalAttributes.mailSubject === 'string'
        ? row.additionalAttributes.mailSubject
        : '';
    // Dispatch names count too, and ALL of them — the server unnests the same
    // column (conversationCont's matchingContacts). Roles are deliberately left
    // out of the haystack on both sides: "driver" is a word, not a person.
    const dispatchNames = (row.contact.dispatchIdentity ?? []).map((actor) => actor.name).join(' ');
    const haystack =
      `${row.contact.name ?? ''} ${row.contact.email ?? ''} ${dispatchNames} ${subject}`.toLowerCase();
    const digits = query.q.replace(/[^0-9]/g, '');
    const phone = (row.contact.phoneNumber ?? '').replace(/[^0-9]/g, '');
    const hit = haystack.includes(needle) || (digits.length >= 3 && phone.includes(digits));
    if (!hit) return false;
  }
  return true;
}

/**
 * The conversation-list store: server-filtered fetch, socket-driven upserts,
 * reconnect reload. With several inboxes selected the API's single `inbox_id`
 * filter forces a fan-out (one request per inbox, merged client-side); paging
 * is only offered in the single-request modes.
 */
export function useConversations(
  source: CommsSource,
  query: ConversationQuery,
  meId: number | null,
): ConversationsState {
  const [rows, setRows] = useState<Conversation[]>([]);
  const [meta, setMeta] = useState<ConversationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRef = useRef(1);
  const ticket = useRef(0);
  const floorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Latest-value refs for the socket handlers — synced in an effect, declared
  // before the load effect so they update first on every commit.
  const queryRef = useRef(query);
  const meIdRef = useRef(meId);
  useEffect(() => {
    queryRef.current = query;
    meIdRef.current = meId;
  });

  const fetchPage = useCallback(
    async (page: number) => {
      const q = queryRef.current;
      const base = {
        status: q.status,
        assigneeType: q.assigneeType,
        botHandled: q.botHandled,
        hasDraft: q.hasDraft,
        sent: q.sent,
        starred: q.starred,
        labels: q.labels?.length ? q.labels : undefined,
        q: q.q || undefined,
        sortBy: q.sortBy,
      } as const;

      // Every list read says, for every row, whether this agent has a draft on
      // it — including `null` where they do not. Feeding that to the store here
      // rather than at each call site is what keeps a row that no longer has a
      // draft from keeping its "Draft" line until the page is reloaded.
      if (q.inboxIds.length > 1) {
        const results = await Promise.all(
          q.inboxIds.map((inboxId) => source.api.listConversations({ ...base, inboxId })),
        );
        const merged = new Map<number, Conversation>();
        for (const result of results) {
          for (const row of result.payload) merged.set(row.id, row);
        }
        const combinedMeta = results.reduce(
          (acc, r) => ({
            mineCount: acc.mineCount + r.meta.mineCount,
            unassignedCount: acc.unassignedCount + r.meta.unassignedCount,
            allCount: acc.allCount + r.meta.allCount,
            pendingCount: acc.pendingCount + r.meta.pendingCount,
            botHandledCount: acc.botHandledCount + r.meta.botHandledCount,
            draftCount: acc.draftCount + r.meta.draftCount,
          }),
          { ...EMPTY_META },
        );
        const rows = sortRows([...merged.values()], q.sortBy);
        seedDraftSummaries(rows);
        return { meta: combinedMeta, payload: rows, pageable: false };
      }

      const result = await source.api.listConversations({
        ...base,
        inboxId: q.inboxIds[0],
        page,
      });
      seedDraftSummaries(result.payload);
      return { meta: result.meta, payload: result.payload, pageable: true };
    },
    [source],
  );

  const load = useCallback(
    (mode: 'query' | 'refresh') => {
      const mine = ++ticket.current;
      const key = cacheKey(source, queryRef.current);
      const startedAt = Date.now();
      if (floorTimer.current) clearTimeout(floorTimer.current);
      pageRef.current = 1;
      setLoading(true);
      setLoadingMore(false);
      setError(null);

      // A cached answer for this exact filter paints now; the request behind it
      // then corrects whatever moved. Only the miss shows a skeleton.
      const cached = mode === 'query' ? cacheRead(key) : null;
      if (cached) {
        setRows(cached.rows);
        setMeta(cached.meta);
        setHasMore(cached.hasMore);
      }
      const showing = mode === 'refresh' || cached !== null;
      setStale(!showing);
      setRefreshing(showing);

      const settle = (apply: () => void) => {
        if (ticket.current !== mine) return;
        apply();
        setLoading(false);
        setStale(false);
        const held = REFRESH_FLOOR_MS - (Date.now() - startedAt);
        // The floor only guards the manual refresh — a filter switch must never
        // sit on data it already has.
        if (mode !== 'refresh' || held <= 0) {
          setRefreshing(false);
          return;
        }
        floorTimer.current = setTimeout(() => {
          if (ticket.current === mine) setRefreshing(false);
        }, held);
      };

      fetchPage(1).then(
        (result) => {
          const more = result.pageable && result.payload.length === PAGE_SIZE;
          settle(() => {
            setRows(result.payload);
            setMeta(result.meta);
            setHasMore(more);
            cacheWrite(key, {
              rows: result.payload,
              meta: result.meta,
              hasMore: more,
              at: Date.now(),
            });
          });
        },
        (e: unknown) =>
          settle(() =>
            setError(e instanceof ApiError ? e : new ApiError('server', 0, 'Something went wrong.')),
          ),
      );
    },
    [fetchPage, source],
  );

  const reload = useCallback(() => load('refresh'), [load]);

  useEffect(() => () => {
    if (floorTimer.current) clearTimeout(floorTimer.current);
  }, []);

  const loadMore = useCallback(() => {
    const mine = ticket.current;
    const nextPage = pageRef.current + 1;
    setLoadingMore(true);
    // The flag is cleared on both paths and regardless of the ticket: a filter
    // change mid-append leaves the button on screen, and a stuck "Loading…"
    // would make it look dead.
    fetchPage(nextPage).then(
      (result) => {
        setLoadingMore(false);
        if (ticket.current !== mine) return;
        pageRef.current = nextPage;
        setRows((current) => {
          const seen = new Set(current.map((row) => row.id));
          return [...current, ...result.payload.filter((row) => !seen.has(row.id))];
        });
        setHasMore(result.pageable && result.payload.length === PAGE_SIZE);
      },
      () => {
        /* keep the current page; the user can retry via reload */
        setLoadingMore(false);
      },
    );
  }, [fetchPage]);

  // The query object is rebuilt by the view every render; key the effect on
  // its scalar fields (inbox set as a joined string) for stability.
  const inboxKey = query.inboxIds.join(',');
  const labelKey = (query.labels ?? []).join(',');
  useEffect(() => {
    // Fetch-on-filter-change is this hook's contract — same as useLoad.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load('query');
  }, [
    load,
    query.status,
    query.assigneeType,
    query.botHandled,
    query.hasDraft,
    query.sent,
    query.starred,
    query.q,
    query.sortBy,
    inboxKey,
    labelKey,
  ]);

  // Socket-driven upserts. Conversation payloads on the socket are hydrated
  // exactly like list rows (lastMessage + unreadCount), so they store as-is.
  useEffect(() => {
    const upsert = (conv: Conversation) => {
      setRows((current) => {
        const matches = matchesQuery(conv, queryRef.current, meIdRef.current);
        const index = current.findIndex((row) => row.id === conv.id);
        if (!matches) {
          return index === -1 ? current : current.filter((row) => row.id !== conv.id);
        }
        const next = index === -1 ? [...current, conv] : current.map((row) => (row.id === conv.id ? conv : row));
        return sortRows(next, queryRef.current.sortBy);
      });
    };

    return source.subscribe((event) => {
      switch (event.name) {
        case 'conversation.created':
        case 'conversation.updated':
        case 'conversation.status_changed':
        case 'assignee.changed':
          upsert(event.payload as Conversation);
          break;
        case 'message.created': {
          const message = event.payload as Message;
          setRows((current) => {
            const index = current.findIndex((row) => row.id === message.conversationId);
            if (index === -1) return current;
            const row = current[index];
            // Already counted. The hydrated `conversation.updated` that follows
            // a message is a separate job on the same queue and can be
            // processed first: its row then carries this message — or a newer
            // one — as `lastMessage`, and an unread count that includes it.
            // Adding one more here is a badge that says 2 for one message
            // until the next list read.
            if (row.lastMessage && row.lastMessage.id >= message.id) return current;
            const unread =
              message.messageType === 'incoming' && !message.private
                ? (row.unreadCount ?? 0) + 1
                : row.unreadCount;
            // Mirrors the server: the provider's own time, and forwards only. An
            // imported old mail must neither date the row to "now" nor take over
            // the preview and drag a live thread down the list.
            const at = message.externalCreatedAt ?? message.createdAt;
            const newer = !row.lastActivityAt || new Date(at) > new Date(row.lastActivityAt);
            const next = [...current];
            next[index] = {
              ...row,
              lastMessage: newer ? message : row.lastMessage,
              lastActivityAt: newer ? at : row.lastActivityAt,
              unreadCount: unread,
            };
            return sortRows(next, queryRef.current.sortBy);
          });
          break;
        }
        case 'contact.updated': {
          // A rename, a new email, dispatch naming a number: the row shows the
          // person, so every row for them takes the fresh copy — and every
          // cached filter with it, or the old name repaints on the next
          // there-and-back switch. Nothing here used to listen at all.
          const contact = event.payload as CrmContact;
          setRows((current) =>
            current.some((row) => row.contact.id === contact.id)
              ? current.map((row) => (row.contact.id === contact.id ? { ...row, contact } : row))
              : current,
          );
          cachePatchContact(contact);
          break;
        }
        case 'message.updated': {
          // The preview line IS the message, so a soft delete — or an inbound
          // the sender edited, on Telegram — has to reach it; nothing else on
          // the row moves. Only the message the row already shows: an older
          // one changing is not the row's business, and this must never bump
          // the unread badge or the activity stamp the way a creation does.
          const message = event.payload as Message;
          setRows((current) => {
            const index = current.findIndex((row) => row.id === message.conversationId);
            if (index === -1 || current[index].lastMessage?.id !== message.id) return current;
            const next = [...current];
            next[index] = { ...current[index], lastMessage: message };
            return next;
          });
          break;
        }
        case 'reconnected':
          load('refresh');
          break;
        case 'access.changed':
          // The grants moved, or a channel came or went. What this list may
          // hold just changed with them, and the rows of a channel removed or
          // revoked a moment ago used to stay on screen — and 404 when opened
          // — until the next filter change happened to re-read.
          load('refresh');
          break;
        default:
          break;
      }
    });
  }, [source, load]);

  const markSeenLocal = useCallback((displayId: number) => {
    setRows((current) =>
      current.map((row) => (row.id === displayId ? { ...row, unreadCount: 0 } : row)),
    );
    cachePatchRow(displayId, { unreadCount: 0 });
  }, []);

  // The server marks more than the loaded page — every conversation in the
  // channel set — so the cached entries for the OTHER filters are zeroed too.
  // Leaving them repaints a stale badge on the next there-and-back switch,
  // which is the one moment the cache exists to make instant. Scoped by inbox
  // for the same reason the request is: a channel that was not marked must not
  // lose its badge here either.
  const markAllSeenLocal = useCallback((inboxIds: number[]) => {
    const covers = (row: Conversation) => inboxIds.length === 0 || inboxIds.includes(row.inboxId);
    const seen = (row: Conversation) => (covers(row) ? { ...row, unreadCount: 0 } : row);
    setRows((current) => current.map(seen));
    for (const [key, entry] of cache) {
      cache.set(key, { ...entry, rows: entry.rows.map(seen) });
    }
  }, []);

  return {
    rows,
    meta,
    loading,
    stale,
    refreshing,
    error,
    hasMore,
    loadingMore,
    loadMore,
    reload,
    markSeenLocal,
    markAllSeenLocal,
  };
}
